import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { ConfirmPage } from 'src/pages/confirm/confirm.component';

import { Util } from 'src/shared/util/util';

import {
  AddressTax,
  CollectingPlatform,
  FILING_STATUS_LABEL,
  FILING_STATUS_TONE,
  PLATFORM_LABEL,
  StatusTone,
  TONE_BG,
  TONE_COLOR,
  TaxFiling,
  TaxesService,
  capitalize,
  filingEstimateNote,
  formatCents,
  formatShortDate,
  frequencyLabel,
  isPlatformCollectable,
  taxPortalUrl,
  taxStatusLabel,
  taxStatusTone,
  translateTax,
} from 'src/shared/services/taxes.service';

interface FilingVM {
  id: number;
  canMarkFiled: boolean;
  calculatedAmount: number;
  period: string;
  statusLabel: string;
  tone: StatusTone;
  dateLine: string;
  amountLine: string;
}

interface PlatformRowVM {
  key: CollectingPlatform;
  label: string;
  icon: string;
  line: string;
}

const PLATFORM_ICON: Record<CollectingPlatform, string> = {
  airbnb: 'assets/img/airbnb-icon.svg',
  vrbo: 'assets/img/vrbo-icon.png',
  booking_com: 'assets/img/booking-com-icon.png',
};

@Component({
  templateUrl: 'tax-filing-details.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TaxFilingDetailsPage implements OnInit {

  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private taxesService = inject(TaxesService);
  private util = inject(Util);
  private modalCtrl = inject(ModalController);
  private windowService = inject(WindowService);
  private demoBlock = inject(DemoBlockService);

  loaded = false;
  loadFailed = false;
  loadFailedText = '';
  canRetryLoad = false;
  isRightSideContent = true;
  propertyId: number;
  taxId: number;
  canRegister = false;
  taxName = '';
  propertyName = '';
  propertyColor = '';
  statusLabel = '';
  statusTone: StatusTone = 'warn';
  statusDetail = '';
  filings: FilingVM[] = [];
  jurisdictionLine = '';
  rateLine = '';
  frequencyLine = '';
  howToFileLine = '';
  licenseLine = '';
  exemptionNote = '';
  showCollection = false;
  platformRows: PlatformRowVM[] = [];
  marketplaceNote = '';
  readonly toneColor = TONE_COLOR;
  readonly toneBg = TONE_BG;

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.propertyId = this.navCtrl.getParam('propertyId') ?? dialogParams?.propertyId;
    this.taxId = this.navCtrl.getParam('taxId') ?? dialogParams?.taxId;
    this.propertyName = this.navCtrl.getParam('propertyName') ?? dialogParams?.propertyName ?? '';
    this.propertyColor = this.navCtrl.getParam('propertyColor') ?? dialogParams?.propertyColor ?? '#72CB9B';
    await this.loadData();
  }

  // On mobile the page stays alive under the pages it opens, so refresh on the way back.
  async ionViewWillEnter() {
    if (this.loaded) {
      await this.loadData();
    }
  }

  private async loadData() {
    try {
      const [tax, filings] = await Promise.all([
        this.taxesService.getTax(this.propertyId, this.taxId),
        this.taxesService.getFilings(this.taxId),
      ]);
      // An unbuilt page would read as an empty tax with Edit and Delete live, so it
      // shows the failure instead, as the property page does.
      this.loadFailed = !tax;
      this.loadFailedText = 'Tax not found';
      this.canRetryLoad = false;
      if (tax) {
        this.buildView(tax, filings);
      }
    } catch {
      this.loadFailed = true;
      this.loadFailedText = 'Failed to load tax';
      this.canRetryLoad = true;
    }
    this.loaded = true;
  }

  readonly boundRetry = () => this.loadData();

  private buildView(tax: AddressTax, filings: TaxFiling[]) {
    const needsRegistration = tax.status === 'pending' || tax.status === 'action_needed';

    this.taxName = tax.name;
    this.rightSidePanelService.setDialogPageTitle(tax.name);
    this.canRegister = needsRegistration;
    this.statusLabel = taxStatusLabel(tax);
    this.statusTone = taxStatusTone(tax);
    this.statusDetail = needsRegistration ? translateTax('Register with the agency to start filing.') : '';
    this.filings = filings.map(filing => this.buildFilingVM(filing));
    this.jurisdictionLine = [tax.jurisdiction || tax.agency, capitalize(tax.category)].filter(Boolean).join(' · ');
    this.rateLine = tax.rate !== null && tax.rate !== undefined
      ? translateTax('%rate%% of booking revenue', { rate: tax.rate })
      : translateTax('Rate not set');
    this.frequencyLine = [frequencyLabel(tax.frequency), tax.due_rule].filter(Boolean).join(' · ') || translateTax('Schedule not set');
    this.howToFileLine = tax.form_number
      ? translateTax('Form %form%', { form: tax.form_number })
      : (taxPortalUrl(tax) || translateTax('Filing details not available'));
    this.licenseLine = tax.license_number
      ? translateTax('Account %number%', { number: tax.license_number })
      : translateTax('No account number on file yet');
    this.showCollection = isPlatformCollectable(tax);
    this.platformRows = (Object.keys(PLATFORM_LABEL) as CollectingPlatform[]).map(key => ({
      key,
      label: translateTax('%platform% bookings', { platform: PLATFORM_LABEL[key] }),
      icon: PLATFORM_ICON[key],
      line: (tax.collecting_platforms || []).includes(key)
        ? translateTax('Collected by %platform%', { platform: PLATFORM_LABEL[key] })
        : translateTax('You collect'),
    }));
    this.marketplaceNote = tax.host_must_file_even_if_platform_collects
      ? translateTax('A return is still required even when platforms collect the tax (zero return).')
      : '';
  }

  private buildFilingVM(filing: TaxFiling): FilingVM {
    return {
      id: filing.id,
      canMarkFiled: filing.status !== 'filed',
      calculatedAmount: filing.calculated_amount,
      period: this.periodLabel(filing),
      statusLabel: FILING_STATUS_LABEL[filing.status],
      tone: FILING_STATUS_TONE[filing.status],
      dateLine: filing.status === 'filed' && filing.filed_on
        ? translateTax('Filed %date%', { date: formatShortDate(filing.filed_on) })
        : filing.due_date
          ? translateTax('Due %date%', { date: formatShortDate(filing.due_date) })
          : translateTax('Deadline not set'),
      amountLine: this.amountLine(filing),
    };
  }

  private periodLabel(filing: TaxFiling): string {
    const start = formatShortDate(filing.period_start);
    const end = formatShortDate(filing.period_end);
    if (start && end) { return `${start} – ${end}`; }
    return start || end || translateTax('Filing');
  }

  private amountLine(filing: TaxFiling): string {
    const platformCollected = filing.platform_collected_amount > 0
      ? translateTax('%amount% platform-collected', { amount: formatCents(filing.platform_collected_amount) })
      : '';
    if (filing.status === 'filed') {
      const parts = [translateTax('%amount% remitted', { amount: formatCents(filing.remitted_amount) })];
      if (platformCollected) {
        parts.push(platformCollected);
      }
      if (filing.confirmation_number) {
        parts.push(translateTax('confirmation %number%', { number: filing.confirmation_number }));
      }
      return parts.join(' · ');
    }
    const parts = [translateTax('Est. %amount% owed', { amount: formatCents(filing.calculated_amount) })];
    if (platformCollected) {
      parts.push(platformCollected);
    }
    const estimateNote = filingEstimateNote(filing);
    if (estimateNote) {
      parts.push(estimateNote);
    }
    return parts.join(' · ');
  }

  editTax() {
    if (this.demoBlock.block()) { return; }
    this.openPage('add-edit-property-tax', { propertyId: this.propertyId, taxId: this.taxId });
  }

  registerTax() {
    if (this.demoBlock.block()) { return; }
    this.openPage('register-property-tax', { taxId: this.taxId, taxName: this.taxName });
  }

  markFiled(filing: FilingVM) {
    if (this.demoBlock.block()) { return; }
    this.openPage('mark-tax-filing-filed', { filingId: filing.id, filingPeriod: filing.period, calculatedAmount: filing.calculatedAmount });
  }

  async confirmDelete() {
    if (this.demoBlock.block()) { return; }
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: {
        title: translateTax('Delete Tax'),
        body: translateTax('Delete this tax? Its filings go away too.'),
        backText: 'Cancel',
        confirmText: 'Delete',
        confirmAction: async () => {
          try {
            await this.taxesService.deleteTax(this.taxId);
            await this.storage.save('didMakePropertyTaxChanges', true);
            this.util.showSuccess('Tax deleted');
            modal.dismiss();
            this.goBack();
          } catch (error) {
            modal.dismiss();
            this.util.showError(error?.error?.message || error?.message || 'Unable to delete the tax');
          }
        },
        confirmButtonClass: 'primary',
      },
      animated: false,
      cssClass: 'confirm-modal',
    });
    modal.present();
  }

  private openPage(route: string, params: any) {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo(route, params);
    } else {
      this.navCtrl.navigateForward(route, params);
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
